// Browser-side cloud account client. Talks ONLY to our own API (apps/api) —
// no third-party identity SDK. The flow is: request a one-time code by email,
// verify it, and receive OUR signed JWT. The JWT is the single credential the
// browser holds; the database is reached only server-side. The token is kept in
// localStorage so a reload stays signed in; signing out clears it.
//
// Everything here degrades gracefully: with no sync API configured, the cloud
// account UI honestly reports that cloud sync is off and the app stays
// fully local-first.
import { appConfig } from './config.js';

const TOKEN_KEY = 'lifeos.cloud.jwt';
const EMAIL_KEY = 'lifeos.cloud.email';
const API_OVERRIDE_KEY = 'lifeos.cloud.apiUrl';

/** The configured API base URL: a runtime override (Settings) wins over the
 * build-time VITE_LIFEOS_SYNC_API_URL. This makes the app resilient to the
 * sync endpoint changing (e.g. a new tunnel URL) without a rebuild. */
export function cloudApiUrl(): string | null {
  let override: string | null = null;
  try {
    override = localStorage.getItem(API_OVERRIDE_KEY);
  } catch {
    /* storage may be unavailable */
  }
  const base = (override && override.trim()) || appConfig.syncApiUrl || '';
  return base ? base.replace(/\/$/, '') : null;
}

/** Read the runtime override (empty string when unset). */
export function getApiOverride(): string {
  try {
    return localStorage.getItem(API_OVERRIDE_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Set/clear the runtime sync-server override. Pass '' to clear. */
export function setApiOverride(url: string): void {
  try {
    const v = url.trim();
    if (v) localStorage.setItem(API_OVERRIDE_KEY, v);
    else localStorage.removeItem(API_OVERRIDE_KEY);
  } catch {
    /* ignore */
  }
}

/** True when an API endpoint is configured and the browser can attempt sign-in. */
export function cloudConfigured(): boolean {
  return cloudApiUrl() !== null;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getSignedInEmail(): string | null {
  try {
    return localStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  return getToken() !== null;
}

function setSession(token: string, email: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email);
  } catch {
    /* storage may be unavailable (private mode); sign-in is best-effort */
  }
}

export function signOut(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

export interface CloudResult<T> {
  readonly ok: boolean;
  readonly error?: string;
  readonly data?: T;
}

async function postJson<T>(path: string, body: unknown): Promise<CloudResult<T>> {
  const base = cloudApiUrl();
  if (!base) return { ok: false, error: 'Cloud sync is not configured on this build.' };
  try {
    const res = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { ok: false, error: typeof data.error === 'string' ? data.error : `Request failed (${res.status}).` };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, error: 'Could not reach the sync server. Check your connection.' };
  }
}

/** Ask the server to email a one-time code. Returns the challenge id to verify against. */
export function requestOtp(email: string): Promise<CloudResult<{ challengeId: string }>> {
  return postJson('/auth/request-otp', { email: email.trim().toLowerCase() });
}

/** Verify the code; on success persist the JWT and signed-in email locally. */
export async function verifyOtp(
  challengeId: string,
  code: string,
  email: string,
): Promise<CloudResult<{ token: string; ownerId: string }>> {
  const result = await postJson<{ token: string; ownerId: string }>('/auth/verify-otp', {
    challengeId,
    code: code.trim(),
  });
  if (result.ok && result.data?.token) setSession(result.data.token, email.trim().toLowerCase());
  return result;
}
